import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { axiosClient } from '../../../../library/Service/axiosClient';
import { useVibrancy } from '../VibrancyDashboardContext';
import { BatchRow, VibrancyProfileService } from '../vibrancyProfileService';
import { HeaderIcon, cardHeaderStyle, cardStyle } from './shared';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Program { id: number; display_name: string; }

interface RangeConfig {
    range1: { label: string; upperBound: number };
    range2: { label: string; upperBound: number };
    range3: { label: string };
}

const DEFAULT_RANGES: RangeConfig = {
    range1: { label: 'Recent (Last 10 Yrs)', upperBound: 10 },
    range2: { label: 'Mid (10–20 Yrs)',      upperBound: 20 },
    range3: { label: 'Senior (20+ Yrs)' },
};

const PRESETS: { name: string; config: RangeConfig | null }[] = [
    { name: 'Default (10/10/20+)', config: DEFAULT_RANGES },
    { name: 'Short (5/10/15+)',    config: { range1: { label: 'Recent (Last 5 Yrs)', upperBound: 5 },  range2: { label: 'Mid (5–15 Yrs)',  upperBound: 15 }, range3: { label: 'Senior (15+ Yrs)' } } },
    { name: 'Long (15/15/30+)',    config: { range1: { label: 'Recent (Last 15 Yrs)', upperBound: 15 }, range2: { label: 'Mid (15–30 Yrs)', upperBound: 30 }, range3: { label: 'Senior (30+ Yrs)' } } },
    { name: 'Custom',              config: null },
];

const EMPTY_RANGE: RangeConfig = {
    range1: { label: '', upperBound: 1 },
    range2: { label: '', upperBound: 2 },
    range3: { label: '' },
};

// ─── Small components ─────────────────────────────────────────────────────────

const SkeletonRows: React.FC = () => (
    <>
        {[1, 2, 3, 4, 5].map(i => (
            <tr key={i}>
                {[1, 2, 3, 4].map(j => (
                    <td key={j} className="px-3 py-2">
                        <span className="placeholder-glow d-block">
                            <span className="placeholder col-8" style={{ height: 12 }} />
                        </span>
                    </td>
                ))}
            </tr>
        ))}
    </>
);

// ─── Main component ───────────────────────────────────────────────────────────

/** VD-06 Batch-wise bifurcation table — programs from broadcast API, data from internal-admin-reports. */
const BatchBifurcation: React.FC = () => {
    const { selectedInstituteId, instituteLoaded } = useVibrancy();

    const [programs,    setPrograms]    = useState<Program[]>([]);
    const [programId,   setProgramId]   = useState<number | null>(null);
    const [rows,        setRows]        = useState<BatchRow[]>([]);
    const [loading,     setLoading]     = useState(false);
    const [ranges,      setRanges]      = useState<RangeConfig>(DEFAULT_RANGES);
    const [showModal,   setShowModal]   = useState(false);

    // Derived range bounds (years → absolute pass-out years)
    const currentYear = new Date().getFullYear();
    const batchStartYear = currentYear - ranges.range2.upperBound; // oldest shown
    const batchEndYear   = currentYear;                            // newest

    // ── Load programs from broadcast server when institute changes ─────────────
    useEffect(() => {
        if (!selectedInstituteId) { setPrograms([]); setProgramId(null); return; }
        axiosClient()
            .get(`${process.env.REACT_APP_BROADCAST_SERVER}/batch/programs/${selectedInstituteId}`)
            .then(r => setPrograms(r.data?.data || []))
            .catch(() => setPrograms([]));
    }, [selectedInstituteId]);

    // ── Load batch data when institute, program, or ranges change ─────────────
    useEffect(() => {
        if (!selectedInstituteId) { setRows([]); return; }
        setLoading(true);
        VibrancyProfileService.getBatchBifurcation(
            selectedInstituteId,
            programId || null,
            batchStartYear,
            batchEndYear,
        )
            .then(r  => setRows(r.data?.data || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [selectedInstituteId, programId, batchStartYear, batchEndYear]);

    if (!instituteLoaded) return null;

    const totalProfiled = rows.reduce((s, r) => s + r.backEndProfiled, 0);

    const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setProgramId(val ? Number(val) : null);
    };

    const handleSaveRanges = (cfg: RangeConfig) => {
        setRanges(cfg);
        setShowModal(false);
        toast.success('Batch ranges saved');
    };

    return (
        <div className="card mb-3" style={cardStyle}>
            {/* Header */}
            <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2" style={cardHeaderStyle}>
                <div className="fw-medium d-flex align-items-center gap-2 font-14">
                    <HeaderIcon icon="📅" bg="#ebf2ff" color="#1b52c4" />Batch-wise Alumni Bifurcation
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="font-13 text-secondary">
                        By pass-out year · Total: {totalProfiled} profiled
                    </span>
                    <span className="text-uppercase fw-bold" style={{ fontSize: 10, color: '#8fa0c0' }}>Program:</span>

                    {/* Program dropdown — populated from broadcast /batch/programs/:id */}
                    <select
                        className="form-select form-select-sm"
                        style={{ fontSize: 11, width: 'auto' }}
                        value={programId ?? ''}
                        onChange={handleProgramChange}
                    >
                        <option value="">All Programs</option>
                        {programs.map(p => (
                            <option key={p.id} value={p.id}>{p.display_name}</option>
                        ))}
                    </select>

                </div>
            </div>

            {/* Table */}
            <div className="table-responsive" style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table className="table table-sm mb-0">
                    <thead className="font-13 text-secondary"
                        style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                        <tr>
                            <th className="text-uppercase fw-medium font-13 text-secondary px-3 py-2">Pass-out Year</th>
                            <th className="text-uppercase fw-medium font-13 text-secondary text-center px-3 py-2">Batch Size</th>
                            <th className="text-uppercase fw-medium font-13 text-secondary text-center px-3 py-2">
                                Front-end <span className="fw-normal text-lowercase" style={{ fontSize: 9 }}>(Portal Active)</span>
                            </th>
                            <th className="text-uppercase fw-medium font-13 text-secondary text-center px-3 py-2" style={{width:150}}>
                                Back-end <span className="fw-normal text-lowercase" style={{ fontSize: 9 }}>(Profiled)</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <SkeletonRows />
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-center text-muted py-4 small">
                                    No batch data found for the selected filters.
                                </td>
                            </tr>
                        ) : (
                            rows.map(r => {
                                const bePct = r.batchSize ? Math.round(r.backEndProfiled / r.batchSize * 100) : 0;
                                const fePct = r.batchSize ? Math.round(r.frontEndActive  / r.batchSize * 100) : 0;
                                const gap   = r.backEndProfiled - r.frontEndActive;
                                return (
                                    <tr key={r.passOutYear}>
                                        <td className="fw-medium px-3 py-2 font-13">{r.passOutYear}</td>
                                        <td className="text-center px-3 py-2 font-13">{r.batchSize}</td>
                                        <td className="text-center px-3 py-2 font-13">
                                            <div className="d-inline-flex align-items-center justify-content-end gap-2">
                                                <span className="fw-semibold text-primary">{r.backEndProfiled}</span>
                                                <span className="font-12 text-secondary" style={{ minWidth: 32, textAlign: 'right' }}>{bePct}%</span>
                                            </div>
                                        </td>
                                        <td className="text-center px-3 py-2 font-13">
                                            <div className="d-inline-flex align-items-center justify-content-end gap-2">
                                                <span className="fw-semibold text-success">{r.frontEndActive}</span>
                                                <span className="font-12 text-secondary" style={{ minWidth: 32, textAlign: 'right' }}>{fePct}%</span>
                                                {gap > 5 && (
                                                    <span
                                                        title={`${gap} profiled but not yet portal-active`}
                                                        className="font-12 text-danger"
                                                    >⚠</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer */}
            <div className="px-3 py-2 border-top d-flex justify-content-between align-items-center flex-wrap"
                style={{ background: '#f4f6fb', borderColor: '#e1e8f5' }}>
                <span className="font-14 text-secondary">
                    Back-end = profiles onboarded in system · Front-end = alumni with active portal login
                </span>
                {programId && (
                    <span className="fw-semibold" style={{ fontSize: 10.5, color: '#1b52c4' }}>
                        Showing {rows.length} batches · {totalProfiled} alumni profiled in this stream
                    </span>
                )}
            </div>

            {/* Edit Ranges Modal */}
            {showModal && (
                <EditRangesModal
                    initial={ranges}
                    onClose={() => setShowModal(false)}
                    onSave={handleSaveRanges}
                />
            )}
        </div>
    );
};

// ─── Edit Ranges Modal (unchanged UI) ────────────────────────────────────────

const EditRangesModal: React.FC<{
    initial: RangeConfig;
    onClose: () => void;
    onSave:  (c: RangeConfig) => void;
}> = ({ initial, onClose, onSave }) => {
    const [draft, setDraft] = useState<RangeConfig>(initial);
    const [error, setError] = useState('');

    const save = () => {
        if (!draft.range1.label.trim() || !draft.range2.label.trim() || !draft.range3.label.trim()) {
            setError('All labels are required.'); return;
        }
        if (draft.range2.upperBound <= draft.range1.upperBound) {
            setError('Range 2 must be greater than Range 1.'); return;
        }
        onSave(draft);
    };

    return (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,.5)', zIndex: 1055 }}>
            <div className="modal-dialog modal-dialog-centered max-600 mx-auto">
                <div className="modal-content radius-15">
                    <div className="modal-body p-4">
                        <h5 className="font-24 font-600 mb-2">✏️ Edit Batch Range Filters</h5>
                        <p className="small text-muted">
                            Set the batch year ranges for this institute. Ranges are stored at institute level and shared across SM and client views.
                        </p>

                        <div className="text-uppercase text-muted small fw-bold mb-2">Quick Presets</div>
                        <div className="d-flex gap-2 flex-wrap mb-3">
                            {PRESETS.map(p => (
                                <button key={p.name} className="btn btn-sm btn-outline-secondary rounded-pill"
                                    onClick={() => { setDraft(p.config ?? EMPTY_RANGE); setError(''); }}>
                                    {p.name}
                                </button>
                            ))}
                        </div>

                        {/* Range 1 */}
                        <div className="bg-light border rounded p-2 mb-2 d-flex align-items-center gap-2 flex-wrap">
                            <span className="fs-5">🟢</span>
                            <input className="form-control form-control-sm flex-grow-1" style={{ maxWidth: 240 }} maxLength={30}
                                value={draft.range1.label}
                                onChange={e => setDraft({ ...draft, range1: { ...draft.range1, label: e.target.value } })} />
                            <span className="small fw-semibold text-muted">≤</span>
                            <input type="number" className="form-control form-control-sm" style={{ width: 70 }} min={1} max={50}
                                value={draft.range1.upperBound}
                                onChange={e => setDraft({ ...draft, range1: { ...draft.range1, upperBound: Number(e.target.value) } })} />
                            <span className="small fw-semibold text-muted">yrs ago</span>
                        </div>

                        {/* Range 2 */}
                        <div className="bg-light border rounded p-2 mb-2 d-flex align-items-center gap-2 flex-wrap">
                            <span className="fs-5">🟡</span>
                            <input className="form-control form-control-sm flex-grow-1" style={{ maxWidth: 240 }} maxLength={30}
                                value={draft.range2.label}
                                onChange={e => setDraft({ ...draft, range2: { ...draft.range2, label: e.target.value } })} />
                            <span className="small fw-semibold text-muted">{draft.range1.upperBound}–</span>
                            <input type="number" className="form-control form-control-sm" style={{ width: 70 }} min={draft.range1.upperBound + 1} max={80}
                                value={draft.range2.upperBound}
                                onChange={e => setDraft({ ...draft, range2: { ...draft.range2, upperBound: Number(e.target.value) } })} />
                            <span className="small fw-semibold text-muted">yrs ago</span>
                        </div>

                        {/* Range 3 (auto) */}
                        <div className="bg-light border rounded p-2 mb-3 d-flex align-items-center gap-2 flex-wrap">
                            <span className="fs-5">🔵</span>
                            <input className="form-control form-control-sm flex-grow-1" style={{ maxWidth: 240 }} maxLength={30}
                                value={draft.range3.label}
                                onChange={e => setDraft({ ...draft, range3: { label: e.target.value } })} />
                            <span className="small fw-semibold text-muted">&gt;</span>
                            <input className="form-control form-control-sm" style={{ width: 70, background: '#f8f9fa' }}
                                value={draft.range2.upperBound} disabled />
                            <span className="small fw-semibold text-muted">yrs ago (auto)</span>
                        </div>

                        {error && <div className="alert alert-danger py-2 small">{error}</div>}

                        <div className="d-flex justify-content-end gap-2 pt-3 border-top">
                            <button className="btn btn-outline-secondary rounded-pill" onClick={onClose}>Cancel</button>
                            <button className="btn btn-green" onClick={save}><span>Apply &amp; Save Ranges</span></button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BatchBifurcation;
